// src/redux/slices/productSlice.ts

import sum from 'lodash/sum';
import { createSlice, Dispatch, PayloadAction } from '@reduxjs/toolkit';
import axios from '../../utils/axios';
import {
  IProductState,
  ICheckoutCartItem,
  IProduct,
} from '../../@types/product';
import { useGetAuthUser } from '../../hooks/useGetAuthUser';

// ----------------------------------------------------------------------
// Initial state
// ----------------------------------------------------------------------
const initialState: IProductState = {
  isLoading: false,
  error: null,
  products: [],
  product: null,
  checkout: {
    activeStep: 0,
    cart: [],
    subtotal: 0,
    total: 0,
    discount: 0,
    shipping: 0,
    billing: null,
    totalItems: 0,
  },
};

// ----------------------------------------------------------------------
// Payload type for adding to cart.
// We have removed any reference to `addOns`, matching ICheckoutCartItem exactly.
// ----------------------------------------------------------------------
type AddToCartPayload = {
  id: string;
  name: string;
  cover: string;
  available: number;
  price: number;
  colors: string[];
  size: any;
  quantity: number; // ← now we accept any positive integer
  addOns?: string[]
};

// ============================================================
//   productSlice Redux : Gère le panier et les produits
// Actions exportées :
//   addToCart      → ajoute ou incrémente la quantité (fusionne même id+size+colors)
//   deleteCart     → supprime une ligne du panier par id
//   increaseQuantity / decreaseQuantity → +1 / -1
//   resetCart      → vide complètement le panier
// Thunks :
//   getProducts()  → GET /api/v1/products/organization/{subOrgId}
//   getProduct()   → GET /api/v1/products/id/{id}
// ============================================================
const slice = createSlice({
  name: 'product',
  initialState,
  reducers: {
    // ───────────────────────────────────────────────────────────────────────
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action: PayloadAction<string | Error>) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // GET PRODUCTS
    getProductsSuccess(state, action: PayloadAction<IProduct[]>) {
      state.isLoading = false;
      state.products = action.payload;
    },

    // GET SINGLE PRODUCT
    getProductSuccess(state, action: PayloadAction<IProduct>) {
      state.isLoading = false;
      state.product = action.payload;
    },

    // ───────────────────────────────────────────────────────────────────────
    // Helper reducer: recalculate cart totals after any change
    recalcCartTotals(state) {
      const cart = state.checkout.cart;
      const totalItems = sum(cart.map((item) => item.quantity));
      const subtotal = sum(cart.map((item) => item.price * item.quantity));

      state.checkout.totalItems = totalItems;
      state.checkout.subtotal = subtotal;
      state.checkout.total =
        subtotal - (state.checkout.discount || 0) + (state.checkout.shipping || 0);
    },

    // ───────────────────────────────────────────────────────────────────────
    // ADD TO CART (now merges same id+size+colors and respects payload.quantity)
    addToCart(state, action: PayloadAction<AddToCartPayload>) {
      const { id, name, cover, available, price, colors, size, quantity } =
        action.payload;

      if (quantity <= 0) {
        // If someone passes quantity=0 or negative, do nothing
        return;
      }

      // Find existing item with same `id`, same `size`, and same `colors` array
      // (If you don't need `colors` to distinguish, you can omit that check.)
      const existingIndex = state.checkout.cart.findIndex(
        (item) =>
          item.id === id &&
          item.size === size &&
          JSON.stringify(item.colors) === JSON.stringify(colors)
      );

      if (existingIndex >= 0) {
        // Already in cart: increment quantity
        const existingItem = state.checkout.cart[existingIndex];
        const newQuantity = existingItem.quantity + quantity;

        state.checkout.cart[existingIndex] = {
          ...existingItem,
          quantity: newQuantity,
          subtotal: price * newQuantity,
        };
      } else {
        // Not in cart yet: create a new line
        const newItem: ICheckoutCartItem = {
          id,
          name,
          cover,
          available,
          price,
          colors,
          size,
          quantity,
          subtotal: price * quantity,
        };
        state.checkout.cart.push(newItem);
      }

      // Recalculate cart totals (totalItems, subtotal, total)
      slice.caseReducers.recalcCartTotals(state);
    },

    // ───────────────────────────────────────────────────────────────────────
    // DELETE an entire line from the cart by product ID
    deleteCart(state, action: PayloadAction<string>) {
      const productIdToRemove = action.payload;
      state.checkout.cart = state.checkout.cart.filter(
        (item) => item.id !== productIdToRemove
      );
      slice.caseReducers.recalcCartTotals(state);
    },

    // ───────────────────────────────────────────────────────────────────────
    // INCREASE quantity of one item BY 1
    increaseQuantity(state, action: PayloadAction<string>) {
      const productId = action.payload;
      const idx = state.checkout.cart.findIndex((item) => item.id === productId);
      if (idx >= 0) {
        const item = state.checkout.cart[idx];
        const newQuantity = item.quantity + 1;
        state.checkout.cart[idx] = {
          ...item,
          quantity: newQuantity,
          subtotal: item.price * newQuantity,
        };
        slice.caseReducers.recalcCartTotals(state);
      }
    },

    // ───────────────────────────────────────────────────────────────────────
    // DECREASE quantity of one item BY 1 (and remove entirely if it drops to 0)
    decreaseQuantity(state, action: PayloadAction<string>) {
      const productId = action.payload;
      const idx = state.checkout.cart.findIndex((item) => item.id === productId);
      if (idx >= 0) {
        const item = state.checkout.cart[idx];
        const newQuantity = item.quantity - 1;
        if (newQuantity <= 0) {
          // Remove the line if quantity ≤ 0
          state.checkout.cart.splice(idx, 1);
        } else {
          state.checkout.cart[idx] = {
            ...item,
            quantity: newQuantity,
            subtotal: item.price * newQuantity,
          };
        }
        slice.caseReducers.recalcCartTotals(state);
      }
    },

    // ───────────────────────────────────────────────────────────────────────
    // RESET the entire cart to empty
    resetCart(state) {
      state.checkout.cart = [];
      state.checkout.billing = null;
      state.checkout.activeStep = 0;
      state.checkout.subtotal = 0;
      state.checkout.total = 0;
      state.checkout.discount = 0;
      state.checkout.shipping = 0;
      state.checkout.totalItems = 0;
    },

    // ───────────────────────────────────────────────────────────────────────
    backStep(state) {
      state.checkout.activeStep = Math.max(0, state.checkout.activeStep - 1);
    },

    nextStep(state) {
      state.checkout.activeStep += 1;
    },

    gotoStep(state, action: PayloadAction<number>) {
      state.checkout.activeStep = action.payload;
    },

    createBilling(state, action: PayloadAction<any>) {
      state.checkout.billing = action.payload;
    },

    applyDiscount(state, action: PayloadAction<number>) {
      state.checkout.discount = action.payload;
      // Recompute total = subtotal − discount + shipping
      state.checkout.total =
        state.checkout.subtotal - state.checkout.discount + state.checkout.shipping;
    },

    applyShipping(state, action: PayloadAction<number>) {
      state.checkout.shipping = action.payload;
      // Recompute total = subtotal − discount + shipping
      state.checkout.total =
        state.checkout.subtotal - state.checkout.discount + state.checkout.shipping;
    },
  },
});

// ──────────────────────────────────────────────────────────────────────────────
// Export the slice's reducer and actions
// ──────────────────────────────────────────────────────────────────────────────
export default slice.reducer;

export const {
  startLoading,
  hasError,
  getProductsSuccess,
  getProductSuccess,
  recalcCartTotals,
  addToCart,
  deleteCart,
  increaseQuantity,
  decreaseQuantity,
  resetCart,
  backStep,
  nextStep,
  gotoStep,
  createBilling,
  applyDiscount,
  applyShipping,
} = slice.actions;

// ──────────────────────────────────────────────────────────────────────────────
// Thunks for fetching products from an API
// ──────────────────────────────────────────────────────────────────────────────
export function getProducts() {
  return async (dispatch: Dispatch) => {
    dispatch(startLoading());
    try {
      // get user via API
      const userResponse = await axios.get('/api/v1/users/my-account');
      const authUser: any = userResponse.data;
      const response = await axios.get(`/api/v1/products/organization/${authUser.subOrgId}?size=100`);
      dispatch(getProductsSuccess(response.data.content));
    } catch (error) {
      dispatch(hasError(error as any));
    }
  };
}

export function getProduct(id: string) {
  return async (dispatch: Dispatch) => {
    dispatch(startLoading());
    try {
      const response = await axios.get(`/api/v1/products/id/${id}`);
      dispatch(getProductSuccess(response.data));
    } catch (error) {
      console.error(error);
      dispatch(hasError(error as any));
    }
  };
}
